import { useState, useEffect } from 'react';
import api from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  BookOpen, Plus, Sparkles, Save, Play, Edit, Trash2, Copy, CheckCircle,
  Target, MessageSquare, Phone, Mail, FileText, Clock, Users, TrendingUp,
  Loader2, ChevronRight, AlertCircle, Lightbulb, Award, BarChart3, Eye, X
} from 'lucide-react';

// Playbook categories
const PLAYBOOK_CATEGORIES = [
  { value: 'discovery', label: 'Discovery Call', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  { value: 'demo', label: 'Product Demo', icon: Play, color: 'bg-purple-100 text-purple-700' },
  { value: 'negotiation', label: 'Negotiation', icon: MessageSquare, color: 'bg-amber-100 text-amber-700' },
  { value: 'closing', label: 'Closing', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'objection', label: 'Objection Handling', icon: AlertCircle, color: 'bg-red-100 text-red-700' },
  { value: 'followup', label: 'Follow-up', icon: Mail, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'onboarding', label: 'Customer Onboarding', icon: Users, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'upsell', label: 'Upsell/Cross-sell', icon: TrendingUp, color: 'bg-pink-100 text-pink-700' }
];

// Sales stages
const SALES_STAGES = [
  'Prospecting', 'Qualification', 'Discovery', 'Demo', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'
];

/**
 * SalesPlaybookPage - AI-powered and manual sales playbooks
 */
export default function SalesPlaybookPage() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('library');
  
  // Create/Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  
  // View dialog (read-only)
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingPlaybook, setViewingPlaybook] = useState(null);
  
  // AI Generation
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCategory, setAiCategory] = useState('discovery');
  const [aiStage, setAiStage] = useState('Discovery');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [generatedPlaybook, setGeneratedPlaybook] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'discovery',
    stage: 'Discovery',
    description: '',
    target_audience: '',
    steps: [],
    objection_handlers: [],
    success_metrics: [],
    tips: []
  });

  useEffect(() => {
    fetchPlaybooks();
  }, []);

  const fetchPlaybooks = async () => {
    try {
      setLoading(true);
      const res = await api.get('/sales/playbooks');
      setPlaybooks(res.data || []);
    } catch (error) {
      // Initialize with empty array if endpoint doesn't exist
      setPlaybooks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlaybook = () => {
    setEditingPlaybook(null);
    setFormData({
      name: '',
      category: 'discovery',
      stage: 'Discovery',
      description: '',
      target_audience: '',
      steps: [{ title: '', description: '', talking_points: [], duration_minutes: 5 }],
      objection_handlers: [],
      success_metrics: [],
      tips: []
    });
    setShowCreateDialog(true);
  };

  const handleEditPlaybook = (playbook) => {
    setEditingPlaybook(playbook);
    setFormData({
      name: playbook.name,
      category: playbook.category,
      stage: playbook.stage,
      description: playbook.description,
      target_audience: playbook.target_audience || '',
      steps: playbook.steps || [],
      objection_handlers: playbook.objection_handlers || [],
      success_metrics: playbook.success_metrics || [],
      tips: playbook.tips || []
    });
    setShowCreateDialog(true);
  };
  
  // View playbook in read-only mode
  const handleViewPlaybook = (playbook) => {
    setViewingPlaybook(playbook);
    setShowViewDialog(true);
  };

  const handleSavePlaybook = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Please provide a name and description');
      return;
    }

    try {
      setSavingPlaybook(true);
      
      if (editingPlaybook) {
        await api.put(`/sales/playbooks/${editingPlaybook.id}`, formData);
        toast.success('Playbook updated successfully');
      } else {
        await api.post('/sales/playbooks', formData);
        toast.success('Playbook created successfully');
      }
      
      setShowCreateDialog(false);
      fetchPlaybooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save playbook');
    } finally {
      setSavingPlaybook(false);
    }
  };

  const handleDeletePlaybook = async (playbookId) => {
    if (!window.confirm('Are you sure you want to delete this playbook?')) return;
    
    try {
      await api.delete(`/sales/playbooks/${playbookId}`);
      toast.success('Playbook deleted');
      fetchPlaybooks();
    } catch (error) {
      toast.error('Failed to delete playbook');
    }
  };

  const handleDuplicatePlaybook = async (playbook) => {
    try {
      await api.post('/sales/playbooks', {
        ...playbook,
        name: `${playbook.name} (Copy)`,
        id: undefined,
        created_at: undefined
      });
      toast.success('Playbook duplicated');
      fetchPlaybooks();
    } catch (error) {
      toast.error('Failed to duplicate playbook');
    }
  };

  const handleGenerateAIPlaybook = async () => {
    if (!aiPrompt) {
      toast.error('Please describe what kind of playbook you need');
      return;
    }

    try {
      setGeneratingAI(true);
      
      const res = await api.post('/sales/playbooks/generate', {
        prompt: aiPrompt,
        category: aiCategory,
        stage: aiStage
      });
      
      setGeneratedPlaybook(res.data);
      toast.success('Playbook generated! Review and save it.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate playbook');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveGeneratedPlaybook = async () => {
    if (!generatedPlaybook) return;
    
    try {
      setSavingPlaybook(true);
      await api.post('/sales/playbooks', generatedPlaybook);
      toast.success('AI Playbook saved to your library');
      setShowAIDialog(false);
      setGeneratedPlaybook(null);
      setAiPrompt('');
      fetchPlaybooks();
    } catch (error) {
      toast.error('Failed to save playbook');
    } finally {
      setSavingPlaybook(false);
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { title: '', description: '', talking_points: [], duration_minutes: 5 }]
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? { ...step, [field]: value } : step)
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const getCategoryInfo = (category) => {
    return PLAYBOOK_CATEGORIES.find(c => c.value === category) || PLAYBOOK_CATEGORIES[0];
  };

  return (
    <div className="space-y-6" data-testid="sales-playbook-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Playbooks</h1>
          <p className="text-muted-foreground">AI-powered and manual sales scripts for every stage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAIDialog(true)}>
            <Sparkles className="h-4 w-4 mr-2" />
            Generate with AI
          </Button>
          <Button onClick={handleCreatePlaybook}>
            <Plus className="h-4 w-4 mr-2" />
            New Playbook
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-700">{playbooks.length}</p>
                <p className="text-sm text-blue-600">Total Playbooks</p>
              </div>
              <BookOpen className="h-10 w-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-purple-700">
                  {playbooks.filter(p => p.ai_generated).length}
                </p>
                <p className="text-sm text-purple-600">AI Generated</p>
              </div>
              <Sparkles className="h-10 w-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-700">
                  {playbooks.filter(p => p.category === 'closing').length}
                </p>
                <p className="text-sm text-emerald-600">Closing Scripts</p>
              </div>
              <Award className="h-10 w-10 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-700">
                  {playbooks.filter(p => p.category === 'objection').length}
                </p>
                <p className="text-sm text-amber-600">Objection Handlers</p>
              </div>
              <AlertCircle className="h-10 w-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="library">Playbook Library</TabsTrigger>
          <TabsTrigger value="by-stage">By Sales Stage</TabsTrigger>
          <TabsTrigger value="objections">Objection Handlers</TabsTrigger>
        </TabsList>

        {/* Library Tab */}
        <TabsContent value="library" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : playbooks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playbooks.map((playbook) => {
                const categoryInfo = getCategoryInfo(playbook.category);
                const CategoryIcon = categoryInfo.icon;
                
                return (
                  <Card key={playbook.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
                          <CategoryIcon className="h-5 w-5" />
                        </div>
                        <div className="flex gap-1">
                          {playbook.ai_generated && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">{playbook.stage}</Badge>
                        </div>
                      </div>
                      <CardTitle className="text-lg mt-2">{playbook.name}</CardTitle>
                      <CardDescription className="line-clamp-2">{playbook.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          {playbook.steps?.length || 0} steps
                        </span>
                        {playbook.objection_handlers?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            {playbook.objection_handlers.length} objections
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="default" className="flex-1" onClick={() => handleViewPlaybook(playbook)} data-testid={`view-playbook-${playbook.id}`}>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEditPlaybook(playbook)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDuplicatePlaybook(playbook)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDeletePlaybook(playbook.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Playbooks Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first sales playbook or generate one with AI
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setShowAIDialog(true)}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </Button>
                  <Button onClick={handleCreatePlaybook}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Manually
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* By Stage Tab */}
        <TabsContent value="by-stage" className="space-y-4">
          {SALES_STAGES.map((stage) => {
            const stagePlaybooks = playbooks.filter(p => p.stage === stage);
            return (
              <Card key={stage}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      {stage}
                    </CardTitle>
                    <Badge variant="outline">{stagePlaybooks.length} playbooks</Badge>
                  </div>
                </CardHeader>
                {stagePlaybooks.length > 0 && (
                  <CardContent>
                    <div className="space-y-2">
                      {stagePlaybooks.map((playbook) => (
                        <div key={playbook.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge className={getCategoryInfo(playbook.category).color}>
                              {getCategoryInfo(playbook.category).label}
                            </Badge>
                            <span className="font-medium">{playbook.name}</span>
                            {playbook.ai_generated && (
                              <Sparkles className="h-4 w-4 text-purple-500" />
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => handleEditPlaybook(playbook)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>

        {/* Objection Handlers Tab */}
        <TabsContent value="objections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Common Objection Handlers</CardTitle>
              <CardDescription>Quick responses to common sales objections</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full">
                {playbooks
                  .filter(p => p.objection_handlers?.length > 0)
                  .flatMap(p => p.objection_handlers.map(oh => ({ ...oh, playbook: p.name })))
                  .slice(0, 10)
                  .map((handler, idx) => (
                    <AccordionItem key={idx} value={`objection-${idx}`}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                          <span>{handler.objection}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 pl-6">
                          <p className="text-sm text-muted-foreground mb-2">
                            From playbook: <span className="font-medium">{handler.playbook}</span>
                          </p>
                          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                            <p className="text-sm font-medium text-emerald-700 mb-1">Response:</p>
                            <p className="text-sm">{handler.response}</p>
                          </div>
                          {handler.followup && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                              <p className="text-sm font-medium text-blue-700 mb-1">Follow-up:</p>
                              <p className="text-sm">{handler.followup}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                {playbooks.filter(p => p.objection_handlers?.length > 0).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No objection handlers yet. Add them to your playbooks!</p>
                  </div>
                )}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Playbook Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>
              {editingPlaybook ? 'Edit Playbook' : 'Create New Playbook'}
            </DialogTitle>
            <DialogDescription>
              Build a structured sales script with steps, talking points, and objection handlers
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-180px)] px-6">
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Playbook Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Enterprise Discovery Call Script"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAYBOOK_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sales Stage</Label>
                <Select value={formData.stage} onValueChange={(val) => setFormData({ ...formData, stage: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SALES_STAGES.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Briefly describe when and how to use this playbook..."
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label>Target Audience</Label>
                <Input
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  placeholder="e.g., Enterprise IT Directors, SMB Owners"
                />
              </div>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Steps</Label>
                <Button size="sm" variant="outline" onClick={addStep}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Step
                </Button>
              </div>
              <div className="space-y-3">
                {formData.steps.map((step, idx) => (
                  <Card key={idx} className="bg-slate-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            value={step.title}
                            onChange={(e) => updateStep(idx, 'title', e.target.value)}
                            placeholder="Step title"
                            className="font-medium"
                          />
                          <Textarea
                            value={step.description}
                            onChange={(e) => updateStep(idx, 'description', e.target.value)}
                            placeholder="What to do in this step..."
                            rows={2}
                          />
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              value={step.duration_minutes}
                              onChange={(e) => updateStep(idx, 'duration_minutes', parseInt(e.target.value))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">minutes</span>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeStep(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          </ScrollArea>
          <DialogFooter className="p-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePlaybook} disabled={savingPlaybook}>
              {savingPlaybook ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingPlaybook ? 'Update Playbook' : 'Create Playbook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Generate Playbook with AI
            </DialogTitle>
            <DialogDescription>
              Describe your sales scenario and AI will create a complete playbook
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[calc(90vh-200px)] px-6">
          {!generatedPlaybook ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={aiCategory} onValueChange={setAiCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAYBOOK_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sales Stage</Label>
                  <Select value={aiStage} onValueChange={setAiStage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SALES_STAGES.map(stage => (
                        <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Describe your playbook needs</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., I need a discovery call script for enterprise SaaS prospects in the healthcare industry. Focus on understanding their current pain points with data security and compliance..."
                  rows={4}
                />
              </div>

              <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-purple-700">Tips for better results:</p>
                    <ul className="list-disc list-inside text-purple-600 mt-1 space-y-1">
                      <li>Mention your industry and target customer type</li>
                      <li>Describe specific pain points you want to address</li>
                      <li>Include any unique selling propositions</li>
                      <li>Mention common objections you face</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-700">Playbook Generated!</span>
                </div>
                <h3 className="font-bold text-lg">{generatedPlaybook.name}</h3>
                <p className="text-sm text-muted-foreground">{generatedPlaybook.description}</p>
              </div>

              <div>
                <Label>Steps ({generatedPlaybook.steps?.length || 0})</Label>
                <div className="space-y-2 mt-2">
                  {generatedPlaybook.steps?.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                      <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{step.title}</p>
                        <p className="text-xs text-muted-foreground">{step.description?.substring(0, 100)}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {generatedPlaybook.objection_handlers?.length > 0 && (
                <div>
                  <Label>Objection Handlers ({generatedPlaybook.objection_handlers.length})</Label>
                  <div className="space-y-1 mt-2">
                    {generatedPlaybook.objection_handlers.map((oh, idx) => (
                      <div key={idx} className="text-sm p-2 bg-amber-50 rounded">
                        <span className="font-medium text-amber-700">{oh.objection}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </ScrollArea>

          <DialogFooter className="p-6 pt-4 border-t">
            {!generatedPlaybook ? (
              <>
                <Button variant="outline" onClick={() => setShowAIDialog(false)}>Cancel</Button>
                <Button onClick={handleGenerateAIPlaybook} disabled={generatingAI || !aiPrompt}>
                  {generatingAI ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Playbook
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setGeneratedPlaybook(null); setAiPrompt(''); }}>
                  Generate Another
                </Button>
                <Button onClick={handleSaveGeneratedPlaybook} disabled={savingPlaybook}>
                  {savingPlaybook ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save to Library
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Playbook Dialog (Read-Only) */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  {viewingPlaybook?.name}
                </DialogTitle>
                <DialogDescription className="mt-2">
                  {viewingPlaybook?.description}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {viewingPlaybook?.ai_generated && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI Generated
                  </Badge>
                )}
                <Badge variant="outline">
                  {viewingPlaybook?.stage}
                </Badge>
              </div>
            </div>
          </DialogHeader>
          
          <ScrollArea className="h-[calc(90vh-200px)] px-6">
            <div className="space-y-6 py-4">
              {/* Meta Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Category</p>
                  <p className="font-medium">{getCategoryInfo(viewingPlaybook?.category)?.label}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Stage</p>
                  <p className="font-medium">{viewingPlaybook?.stage}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Steps</p>
                  <p className="font-medium">{viewingPlaybook?.steps?.length || 0}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Target Audience</p>
                  <p className="font-medium">{viewingPlaybook?.target_audience || 'General'}</p>
                </div>
              </div>
              
              {/* Steps Section */}
              {viewingPlaybook?.steps?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Playbook Steps
                  </h3>
                  <div className="space-y-4">
                    {viewingPlaybook.steps.map((step, idx) => (
                      <Card key={idx} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full font-bold shrink-0">
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-base">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{step.description}</p>
                              {step.duration_minutes && (
                                <div className="flex items-center gap-1 mt-2 text-sm text-blue-600">
                                  <Clock className="h-4 w-4" />
                                  <span>{step.duration_minutes} minutes</span>
                                </div>
                              )}
                              {step.key_questions?.length > 0 && (
                                <div className="mt-3 bg-blue-50 p-3 rounded-lg">
                                  <p className="text-sm font-medium text-blue-700 mb-2">Key Questions:</p>
                                  <ul className="list-disc list-inside text-sm space-y-1">
                                    {step.key_questions.map((q, qIdx) => (
                                      <li key={qIdx}>{q}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {step.talking_points?.length > 0 && (
                                <div className="mt-3 bg-emerald-50 p-3 rounded-lg">
                                  <p className="text-sm font-medium text-emerald-700 mb-2">Talking Points:</p>
                                  <ul className="list-disc list-inside text-sm space-y-1">
                                    {step.talking_points.map((tp, tpIdx) => (
                                      <li key={tpIdx}>{tp}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Objection Handlers */}
              {viewingPlaybook?.objection_handlers?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    Objection Handlers
                  </h3>
                  <Accordion type="multiple" className="w-full">
                    {viewingPlaybook.objection_handlers.map((handler, idx) => (
                      <AccordionItem key={idx} value={`objection-${idx}`}>
                        <AccordionTrigger className="text-left hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="flex items-center justify-center w-6 h-6 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                              {idx + 1}
                            </span>
                            <span className="font-medium">{handler.objection}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 pl-8">
                            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                              <p className="text-sm font-medium text-emerald-700 mb-1">Recommended Response:</p>
                              <p className="text-sm whitespace-pre-wrap">{handler.response}</p>
                            </div>
                            {handler.followup && (
                              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                                <p className="text-sm font-medium text-blue-700 mb-1">Follow-up:</p>
                                <p className="text-sm whitespace-pre-wrap">{handler.followup}</p>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
              
              {/* Success Metrics */}
              {viewingPlaybook?.success_metrics?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-emerald-600" />
                    Success Metrics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {viewingPlaybook.success_metrics.map((metric, idx) => (
                      <div key={idx} className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-600" />
                          <span className="font-medium">{metric.name || metric}</span>
                        </div>
                        {metric.target && (
                          <p className="text-sm text-emerald-600 ml-6">Target: {metric.target}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tips */}
              {viewingPlaybook?.tips?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-500" />
                    Pro Tips
                  </h3>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <ul className="space-y-2">
                      {viewingPlaybook.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-amber-500 mt-0.5">💡</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter className="p-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowViewDialog(false);
              handleEditPlaybook(viewingPlaybook);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Playbook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
